// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import axios from 'axios';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

import { useUser } from '@store';
import config from '../../../config.frontend';
import { useInsertPaymentMethodMutation } from '../queries/insertPaymentMethod.mutation.generated';
import { useUpdatePaymentMethodByPkMutation } from '../queries/updatePaymentMethod.mutation.generated';
import { useUpdatePrimaryPaymentMethodMutation } from '../queries/updatePrimaryPaymentMethod.mutation.generated';
import { toPaymentMethodRow, type SavedPaymentMethodDetails } from '../paymentMethodRow';
import { pendingBankSetup, pendingBankRow, microdepositsSentence, microdepositEntry, type PendingBankSetup } from '../bankVerification';

// TYPES ---------------------------------------------------------------- //

/** The two kinds of method a dealer can save. 'bank' is a US bank account, debited by ACH. */
export type StripeMethodKind = 'card' | 'bank';

interface StripePaymentMethodFormProps {
  customerId: number;
  /** Set when replacing an existing row's method; absent when adding one. */
  id?: number;
  name: string;
  kind: StripeMethodKind;
  primaryExists: boolean;
  handleClose: () => void;
}

// HELPERS ---------------------------------------------------------------- //

// Stripe.js is loaded once per page, never per form mount.
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(config.stripePublishableKey ?? '');
  return stripePromise;
};

const errorText = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { error?: string } }; message?: string } | undefined;
  return e?.response?.data?.error || e?.message || fallback;
};

/** Sends a failed save to Sentry, tagged so failures can be counted by customer and kind. */
const reportSaveFailure = (stage: 'start' | 'save', err: unknown, customerId: number, kind: StripeMethodKind) => {
  Sentry.captureException(err, {
    tags: { feature: 'stripe-payment-method', stage, kind },
    extra: { customerId, message: errorText(err, '') },
  });
};

// COMPONENT ---------------------------------------------------------------- //

/**
 * Saves a card or US bank account with Stripe for a dealership.
 *
 * The server opens a SetupIntent for the kind being added (createStripeSetupIntent) and this
 * form mounts Stripe's Payment Element on its client secret. Stripe collects the details in its
 * own iframe — a card number never touches this app — and, for a bank account, verifies it
 * (through the bank's login, or by micro-deposits when entered by account number) and shows
 * the ACH debit mandate. On confirmation the server reads the method
 * back from Stripe (describeStripePaymentMethod) and the row is written from that.
 */
const StripePaymentMethodForm: React.FC<StripePaymentMethodFormProps> = props => {
  const { customerId, kind, id } = props;
  const user = useUser();

  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [setupError, setSetupError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setClientSecret(null);
    setSetupError(null);

    const start = async () => {
      try {
        const token = await user?.getToken();
        if (!token) throw new Error('Could not confirm your sign-in. Reload the page and try again.');
        const res = await axios.post(
          '/.netlify/functions/createStripeSetupIntent',
          // Replacing a method offers the bank login only: a bank account entered by account
          // number waits on micro-deposits and could not take over the row being replaced.
          { customerId, kind, replacing: Boolean(id) },
          { headers: { authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setClientSecret(res.data?.clientSecret || null);
      } catch (err) {
        console.error('Could not start saving a payment method:', err);
        reportSaveFailure('start', err, customerId, kind);
        if (!cancelled) setSetupError(errorText(err, 'Could not start saving a payment method.'));
      }
    };
    start();

    return () => {
      cancelled = true;
    };
  }, [customerId, kind, id, user]);

  if (!config.stripePublishableKey) {
    return <Alert severity='error'>Payments are not configured for this environment (the Stripe publishable key is missing).</Alert>;
  }
  if (setupError) return <Alert severity='error'>{setupError}</Alert>;
  if (!clientSecret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Elements stripe={getStripe()} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <SetupForm {...props} />
    </Elements>
  );
};

/** The half that needs Stripe's Elements context: the Payment Element and the Save button. */
const SetupForm: React.FC<StripePaymentMethodFormProps> = ({ customerId, id, name, kind, primaryExists, handleClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const user = useUser();

  const [insertPaymentMethod] = useInsertPaymentMethodMutation();
  const [updatePaymentMethod] = useUpdatePaymentMethodByPkMutation();
  const [updatePrimaryPaymentMethod] = useUpdatePrimaryPaymentMethodMutation();

  const [ready, setReady] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // A bank account that never gets saved usually means the dealer closed Stripe's bank login
  // without finishing it (or entering the account by hand), which raises no error.
  // Report it on close so those attempts are countable. `complete` says whether the bank was linked.
  const saved = React.useRef(false);
  const complete = React.useRef(false);
  React.useEffect(() => {
    if (kind === 'card') return undefined;
    return () => {
      if (saved.current) return;
      Sentry.captureMessage('Bank account not saved', {
        level: 'info',
        tags: { feature: 'stripe-payment-method', stage: 'abandoned', kind, linked: String(complete.current) },
        extra: { customerId },
      });
    };
  }, [kind, customerId]);

  // Stripe confirmed a bank account for micro-deposits but the row could not be written (or the
  // method was being replaced, which this path refuses): cancel the SetupIntent so Stripe sends no
  // deposits and no "verify your account" email for a method that does not exist here.
  const cancelPendingSetup = async (setupIntentId: string) => {
    try {
      const token = await user?.getToken();
      await axios.post(
        '/.netlify/functions/cancelStripeBankSetup',
        { customerId, setupIntentId },
        { headers: { authorization: `Bearer ${token}` } }
      );
    } catch (cancelErr) {
      console.error('Could not cancel the bank account setup after a failed save:', cancelErr);
      Sentry.captureException(cancelErr, { tags: { feature: 'stripe-payment-method', stage: 'cancel-after-failed-save', kind } });
    }
  };

  const save = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    let pendingSetup: PendingBankSetup | null = null;
    try {
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        // Cards and bank accounts confirm in place (a bank needing micro-deposits comes back at
        // requires_action, not as a redirect); the return URL is only for a method that would
        // need a redirect, which none of ours does.
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      });
      if (confirmError) throw new Error(confirmError.message || 'Stripe could not save this payment method.');

      // A bank account entered by routing and account number: Stripe sends micro-deposits and
      // the dealer confirms them later. Saved now as a new, non-primary row Stripe can't yet
      // debit (pending_setup_intent_id); the Billing page finishes it once they're confirmed.
      const pending = kind !== 'card' ? pendingBankSetup(setupIntent) : null;
      if (pending) {
        pendingSetup = pending;
        // Replacing asks Stripe for the bank login only (createStripeSetupIntent), so this is a
        // safety net: a replacement waiting on deposits can't take over the row, and must not be
        // saved beside the method it was meant to replace.
        if (id) {
          throw new Error(
            'A bank account entered by account number can’t replace this method. Add it as a new payment method, verify it, then remove this one.'
          );
        }
        const token = await user?.getToken();
        const res = await axios.post(
          '/.netlify/functions/describeStripePaymentMethod',
          { customerId, setupIntentId: pending.setupIntentId },
          { headers: { authorization: `Bearer ${token}` } }
        );
        await insertPaymentMethod({
          variables: { customerId, name, ...pendingBankRow(res.data as SavedPaymentMethodDetails, pending.setupIntentId) },
        });
        saved.current = true;
        toast.info(
          `Bank account saved. ${microdepositsSentence(pending.microdepositType)} After that, choose Verify on this page and ${microdepositEntry(pending.microdepositType)}. It can’t be charged until then.`,
          { autoClose: false }
        );
        handleClose();
        return;
      }

      if (setupIntent?.status !== 'succeeded' || typeof setupIntent.payment_method !== 'string') {
        throw new Error(
          `Stripe left this method at "${setupIntent?.status ?? 'unknown'}", so it was not saved. Try a different card or bank account.`
        );
      }

      const token = await user?.getToken();
      const res = await axios.post(
        '/.netlify/functions/describeStripePaymentMethod',
        { customerId, paymentMethodId: setupIntent.payment_method },
        { headers: { authorization: `Bearer ${token}` } }
      );
      const row = toPaymentMethodRow(res.data as SavedPaymentMethodDetails);

      if (id) {
        await updatePaymentMethod({ variables: { id, name, ...row } });
      } else {
        const res = await insertPaymentMethod({ variables: { customerId, name, primary: !primaryExists, ...row } });
        const newId = res.data?.insert_arpaymentmethods?.returning?.[0]?.id;
        // Taking over as primary also clears the flag on every other method, including an iCheck
        // primary, so autopay (which charges the one primary) reaches the method just saved.
        if (!primaryExists && newId) await updatePrimaryPaymentMethod({ variables: { customerId, id: newId } });
      }
      saved.current = true;
      toast.success('Payment method saved');
      handleClose();
    } catch (err) {
      console.error('Failed to save payment method:', err);
      reportSaveFailure('save', err, customerId, kind);
      if (pendingSetup && !saved.current) await cancelPendingSetup(pendingSetup.setupIntentId);
      setError(errorText(err, 'Failed to save payment method.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PaymentElement
        options={{ layout: 'tabs' }}
        onReady={() => setReady(true)}
        onChange={e => {
          complete.current = e.complete;
        }}
      />
      {error ? (
        <Alert severity='error' sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant='contained' color='primary' disabled={!ready || saving || !stripe} onClick={save}>
          {saving ? 'Saving…' : kind === 'card' ? 'Save Card' : 'Save Bank Account'}
        </Button>
      </Box>
    </>
  );
};

export default StripePaymentMethodForm;
